/* eslint-disable @typescript-eslint/ban-ts-comment */
import React from "react";
import CssBaseline from "@mui/material/CssBaseline";
import NavigateNext from "@mui/icons-material/NavigateNext";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import ButtonGroup from "@mui/material/ButtonGroup";
import { graphql } from "gatsby";

import { ChapterVerseLocation } from "../types/ChapterVerseLocation";
import GetChapter from "../components/GetChapter";
import ProgressBar from "../components/ProgressBar";
import AppBarMain from "../components/AppBarMain";
import Search from "../components/Search";
import MetaTags from "../components/MetaTags";
import QuranText from "../components/QuranText";
import { Edition } from "../enums/Edition";
import { Chapter } from "../types/Chapter";
import { Recitation } from "../types/Recitation";
import { VerseLocationForText } from "../types/VerseLocationForText";
import ShareVersesDialog from "../components/ShareVersesDialog";
import Snackbar from "@mui/material/Snackbar";
import MenuChapters from "../components/MenuChapters";
import FontScalingMenu from "../components/FontScalingMenu";
import { topAppBarHeight } from "../constants/BaseConstants";

// --- Hoisted static sx objects (prevents re-hashing every render) ---
const mainSx = {
  backgroundColor: "#ffffff",
  margin: "20px 14px 20px 14px",
  "@media(min-width: 400px)": {
    margin: "20px 22px 20px 22px",
  },
  padding: "0px",
} as const;

const contentSx = { maxWidth: "750px", margin: "auto" } as const;
const toolbarSx = { height: topAppBarHeight } as const;

const buttonSx = {
  color: "#333",
  backgroundColor: "#d8d8d8",
  "&:hover": { backgroundColor: "#c0c0c0" },
  paddingLeft: "5px",
  paddingRight: "5px",
} as const;

const buttonGroupSx = { margin: "25px 0px 0px 10px" } as const;

// --- globals kept as-is ---
let forceNewVerseNumberForText = 0;
let bookmark: ChapterVerseLocation;

// Lazy-load heavy audio component (cuts initial JS)
const AudioComponent = React.lazy(() => import("../components/AudioComponent"));

function QuranChapter(props: unknown): JSX.Element {
  const { startTransition } = React; // schedule non-urgent updates

  // Stable recitations list
  const recitations = React.useMemo<Recitation[]>(
    () => [
      {
        name: "Brian",
        name2: "Adnan",
        code: "polly-brian",
        urlGod: "https://data.surequran.com/polly-brian-g/",
        urlAllah: "https://data.surequran.com/polly-brian-a/",
      },
    ],
    [],
  );

  // Body background (SSR-safe)
  React.useEffect(() => {
    if (typeof document !== "undefined") {
      const prev = document.body.style.backgroundColor;
      document.body.style.backgroundColor = "#ffffff";
      return () => {
        document.body.style.backgroundColor = prev;
      };
    }
  }, []);

  // @ts-ignore:
  const chaptersInfo = props.data.allQuranInfoJson.nodes;
  const defaultRecitationCode = "polly-brian";
  const defaultEdition = Edition.God;
  const defaultFontScaling = 100;
  // @ts-ignore:
  const defaultChapter = props.data.allTranslationJson.nodes[0] as Chapter;

  // --- state ---
  const [edition, setEdition] = React.useState(defaultEdition);
  const [recitationCode, setRecitationCode] = React.useState(defaultRecitationCode);
  const [fontScaling, setFontScaling] = React.useState(defaultFontScaling);

  const [isPlaying, setIsPlaying] = React.useState(false);

  const [chapter, setChapter] = React.useState(defaultChapter);
  const [verseNumberForAudio, setVerseNumberForAudio] = React.useState(defaultChapter.verses[0].verseNumber);
  const [isProgressBarVisible, setIsProgressBarVisible] = React.useState(false);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);

  const defaultVerseLocationForText = new VerseLocationForText(defaultChapter.verses[0].verseNumber, false, false, 0);
  const defaultChapterVerseLocation = new ChapterVerseLocation(defaultChapter.chapterNumber, defaultVerseLocationForText);

  const [verseLocationForText, setVerseLocationForText] = React.useState(defaultVerseLocationForText);
  const [chapterVerseLocation, setChapterVerseLocation] = React.useState(defaultChapterVerseLocation);

  const [isShareVersesDialogOpen, setIsShareVersesDialogOpen] = React.useState(false);
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const [anchorChapterMenu, setAnchorChapterMenu] = React.useState<HTMLElement | null>(null);
  const [anchorFontScalingMenu, setAnchorFontScalingMenu] = React.useState<HTMLElement | null>(null);
  const [isContentVisible, setIsContentVisible] = React.useState(true); // hide during top scroll to eliminate flicker

  // Initialize from localStorage + URL hash
  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const getJson = <T,>(k: string, fallback: T) => {
      try {
        const v = window.localStorage.getItem(k);
        return v == null ? fallback : (JSON.parse(v) as T);
      } catch {
        return fallback;
      }
    };

    bookmark = getJson<ChapterVerseLocation | null>("BOOKMARK", null) ?? null;
    setEdition(getJson<Edition>("EDITION", defaultEdition));
    setRecitationCode(getJson<string>("RECITATION-CODE", defaultRecitationCode));
    setFontScaling(getJson<number>("FONT-SCALING", defaultFontScaling));

    const hash = window.location.hash?.slice(1);
    if (hash) {
      const vNum = Number(hash);
      if (
        !Number.isNaN(vNum) &&
        vNum >= defaultChapter.verses[0].verseNumber &&
        vNum <= defaultChapter.verses[defaultChapter.verses.length - 1].verseNumber
      ) {
        setVerseLocationForText(new VerseLocationForText(vNum, true, true, 0));
      }
    }
  }, [defaultChapter, defaultEdition]);

  // Persist to localStorage
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem("EDITION", JSON.stringify(edition));
      window.localStorage.setItem("RECITATION-CODE", JSON.stringify(recitationCode));
      window.localStorage.setItem("FONT-SCALING", JSON.stringify(fontScaling));
    } catch {
      // ignore quota errors
    }
  }, [edition, recitationCode, fontScaling]);

  // --- handlers/helpers (memoized) ---
  const getRecitation = React.useCallback(
    (code: string): Recitation => {
      try {
        const r = recitations.find(r => r.code === code);
        return r ?? getRecitation(defaultRecitationCode);
      } catch {
        return getRecitation(defaultRecitationCode);
      }
    },
    [recitations],
  );

  const handlePlayPause = React.useCallback(() => {
    startTransition(() => {
      if (!isPlaying) {
        const v = verseLocationForText;
        const verseLocation = new VerseLocationForText(v.verseNumber, true, true, ++forceNewVerseNumberForText);
        setVerseLocationForText(verseLocation);
      }
      setIsPlaying(p => !p);
    });
  }, [isPlaying, verseLocationForText, startTransition]);

  const handleNewChapter = React.useCallback(
    (newChNum: number) => {
      setAnchorChapterMenu(null);
      startTransition(() => {
        const vlt = new VerseLocationForText(newChNum === 1 || newChNum === 9 ? 1 : 0, true, false, ++forceNewVerseNumberForText);
        if (newChNum === chapter.chapterNumber) {
          setVerseLocationForText(vlt);
        } else {
          setIsProgressBarVisible(true);
          setChapterVerseLocation(new ChapterVerseLocation(newChNum, vlt));
        }
      });
    },
    [chapter.chapterNumber, startTransition],
  );

  function handleNextButtonClicked() {
    handleNewChapter(chapter.chapterNumber < 114 ? chapter.chapterNumber + 1 : 1);
  }

  function handlePrevButtonClicked() {
    handleNewChapter(chapter.chapterNumber > 1 ? chapter.chapterNumber - 1 : 114);
  }

  function getNextChapter() {
    handleNewChapter(chapter.chapterNumber < 114 ? chapter.chapterNumber + 1 : 1);
  }

  const handleVerseClicked = React.useCallback(
    (vNumber: number) => {
      startTransition(() => {
        setVerseLocationForText(
          v => new VerseLocationForText(vNumber, false, false, vNumber === v.verseNumber ? ++forceNewVerseNumberForText : 0),
        );
      });
    },
    [startTransition],
  );

  const handleAudioEnded = React.useCallback(() => {
    // @ts-ignore
    window.gtag?.("event", "mp3_file_loaded");
    const last = chapter.verses[chapter.verses.length - 1].verseNumber;
    if (verseNumberForAudio < last) {
      setVerseLocationForText(new VerseLocationForText(verseNumberForAudio + 1, true, true, 0));
    } else {
      getNextChapter();
    }
  }, [chapter, verseNumberForAudio]);

  const handleChapterRenderingDone = React.useCallback(() => {
    setIsProgressBarVisible(false);
    setVerseLocationForText(chapterVerseLocation.verseLocationForText);
    setIsContentVisible(true);
  }, [chapterVerseLocation]);

  // Throttle history writes to next frame
  const replaceHashRaf = React.useRef<number | null>(null);
  const handleVerseScrollingDone = React.useCallback((verseNumber: number) => {
    setVerseNumberForAudio(verseNumber);
    if (typeof window !== "undefined") {
      if (replaceHashRaf.current) cancelAnimationFrame(replaceHashRaf.current);
      replaceHashRaf.current = requestAnimationFrame(() => {
        window.history.replaceState(null, "", "#" + verseNumber.toString());
      });
    }
  }, []);

  const gotoLocation = React.useCallback(
    (gotoLoc: ChapterVerseLocation) => {
      setIsSearchOpen(false);
      if (chapter.chapterNumber === gotoLoc.chapterNumber) {
        setVerseLocationForText(gotoLoc.verseLocationForText);
      } else {
        setChapterVerseLocation(gotoLoc);
      }
    },
    [chapter.chapterNumber],
  );

  const handleNewChapterLoaded = React.useCallback(
    (newChapter: Chapter) => {
      if (newChapter.chapterNumber === chapterVerseLocation.chapterNumber) {
        // @ts-ignore
        window.gtag?.("event", "chapter_loaded");
        setChapter(newChapter);
        setVerseLocationForText(chapterVerseLocation.verseLocationForText);

        if (typeof window !== "undefined") {
          requestAnimationFrame(() => {
            window.history.replaceState(null, "", "/" + newChapter.chapterNumber.toString());
          });
        }

        setIsContentVisible(false);
        window.setTimeout(() => setIsContentVisible(true), 300);
      }
    },
    [chapterVerseLocation],
  );

  const handleBookmarkClicked = React.useCallback(() => {
    const verseLocation = new VerseLocationForText(verseNumberForAudio, true, true, 0);
    bookmark = new ChapterVerseLocation(chapter.chapterNumber, verseLocation);
    try {
      window.localStorage.setItem("BOOKMARK", JSON.stringify(bookmark));
    } catch {}
    setOpenSnackbar(true);
  }, [chapter.chapterNumber, verseNumberForAudio]);

  const handleGotoBookmarkClicked = React.useCallback(() => {
    if (bookmark != null) {
      gotoLocation(bookmark);
    }
  }, [gotoLocation]);

  const handleShareVersesClicked = React.useCallback(() => {
    setIsShareVersesDialogOpen(true);
  }, []);

  const handleCloseSnackbar = React.useCallback(() => {
    setOpenSnackbar(false);
  }, []);

  // helpers for preloading next verse audio
  const z3 = (n: number) => String(n).padStart(3, "0");
  const buildAudioUrl = (chNum: number, vNum: number) => {
    const base = edition === Edition.God ? getRecitation(recitationCode).urlGod : getRecitation(recitationCode).urlAllah;
    return `${base}${z3(chNum)}-${z3(vNum)}.mp3`;
  };

  return (
    <Box sx={mainSx}>
      <Box sx={toolbarSx} />

      {/* When hidden, we ask the browser to skip layout/paint for the block */}
      <Box
        sx={{
          ...contentSx,
          visibility: isContentVisible ? "visible" : "hidden",
          // TS cast so sx accepts draft CSS properties without @types updates
          ...({
            contentVisibility: isContentVisible ? "visible" : "auto",
            containIntrinsicSize: "1000px 2000px",
          } as unknown as React.CSSProperties),
        }}
        aria-hidden={isContentVisible ? undefined : true}
      >
        {/* stable handler */}
        <QuranText
          chapter={chapter}
          edition={edition}
          fontScaling={fontScaling}
          verseClicked={handleVerseClicked}
          /* stable handler */
          chapterRenderingDone={handleChapterRenderingDone}
          /* throttled replaceState */
          verseScrollingDone={handleVerseScrollingDone}
          verseLocation={verseLocationForText}
          handleEditionChange={setEdition}
          contentVisibilityChanged={setIsContentVisible}
        />

        <ButtonGroup variant="contained" size="medium" sx={buttonGroupSx} color="info">
          <Button sx={buttonSx} onClick={handlePrevButtonClicked} aria-label="Prev Chapter">
            <NavigateBefore />
          </Button>
          <Button sx={buttonSx} onClick={handleNextButtonClicked} aria-label="Next Chapter">
            &ensp;Next <NavigateNext />
          </Button>
        </ButtonGroup>
      </Box>

      <AppBarMain
        chapter={chapter}
        isAudioPlaying={isPlaying}
        onClickPlayPauseButton={handlePlayPause}
        onClickSearchButton={() => setIsSearchOpen(true)}
        onClickTextSizeButton={(event: React.MouseEvent<HTMLElement>) => {
          setAnchorFontScalingMenu(event.currentTarget);
        }}
        bookmarkClicked={handleBookmarkClicked}
        gotoBookmarkClicked={handleGotoBookmarkClicked}
        shareVersesClicked={handleShareVersesClicked}
        onClickTitleBlock={(event: React.MouseEvent<HTMLElement>) => {
          setAnchorChapterMenu(event.currentTarget);
        }}
      />

      {/* Real audio component (lazy-loaded) */}
      <React.Suspense fallback={null}>
        <AudioComponent
          isForCaching={false}
          isAudioPlaying={isPlaying}
          chapter={chapter}
          verseNumber={verseNumberForAudio}
          verseRepeat={1}
          edition={edition}
          recitation={getRecitation(recitationCode)}
          audioEnded={handleAudioEnded}
        />
      </React.Suspense>

      {/* Preload next verse so playback is seamless */}
      {isPlaying && verseNumberForAudio < chapter.verses[chapter.verses.length - 1].verseNumber && (
        <link rel="prefetch" href={buildAudioUrl(chapter.chapterNumber, verseNumberForAudio + 1)} crossOrigin="anonymous" />
      )}

      {/* Prefetch JSON for requested + next chapter */}
      {chapterVerseLocation.chapterNumber !== chapter.chapterNumber && (
        <GetChapter chapterNumber={chapterVerseLocation.chapterNumber} newChapterLoaded={handleNewChapterLoaded} />
      )}
      {chapterVerseLocation.chapterNumber !== chapter.chapterNumber && (
        <GetChapter chapterNumber={chapterVerseLocation.chapterNumber + 1} newChapterLoaded={handleNewChapterLoaded} />
      )}

      <Box m={8} />

      {/* dialogs/menus/search (kept simple; no extra lazy to reduce churn) */}
      <ShareVersesDialog
        currentVerseNumber={verseLocationForText.verseNumber}
        chapter={chapter}
        isOpen={isShareVersesDialogOpen}
        handleCloseDialog={() => setIsShareVersesDialogOpen(false)}
        edition={edition}
      />

      <Search fontScaling={fontScaling} isOpen={isSearchOpen} handleClose={() => setIsSearchOpen(false)} searchVerseClick={gotoLocation} />

      <MenuChapters
        anchor={anchorChapterMenu}
        chaptersInfo={chaptersInfo}
        chapterClicked={(chapterIndex: number) => handleNewChapter(chapterIndex + 1)}
        handleMenuClose={() => {
          setAnchorChapterMenu(null);
        }}
        currentChapterNumber={chapter.chapterNumber}
      />

      <FontScalingMenu
        anchor={anchorFontScalingMenu}
        selectedFontScaling={fontScaling}
        handleFontScalingChange={(font: number) => {
          setFontScaling(font);
          setAnchorFontScalingMenu(null);
        }}
        handleMenuClose={() => setAnchorFontScalingMenu(null)}
      />

      <ProgressBar isVisible={isProgressBarVisible} />
      <MetaTags chapter={chapter} />
      <CssBaseline />

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        message={"Verse " + chapter.chapterNumber + ":" + verseLocationForText.verseNumber + " bookmarked"}
      />
    </Box>
  );
}

export default QuranChapter;

export const quranQuery = graphql`
  query QuranInfoJson2($slug: Int!) {
    allTranslationJson(filter: { chapterNumber: { eq: $slug } }) {
      nodes {
        verses {
          verseNumber
          verseString
        }
        nameEnglish
        nameTrans
        nameArabic
        chapterNumber
      }
    }
    allQuranInfoJson {
      nodes {
        nVerses
        nameEnglish
        nameTrans
        nameArabic
      }
    }
  }
`;
